import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { APIWebhook } from '../../../../../../../../typings/Webhook.interface';
import { HookdeckAPIError } from '../../../../../client/Hookdeck';
import field_formats from '../../../../../utils/field-formatters';
import { cleanseFormErrorObject } from '../../../../../utils/form';
import Button from '../../../../common/base/Button';
import { StyledCardSection } from '../../../../common/base/Card';
import Icon from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import DropdownMenu, { DropdownMenuOption } from '../../../../common/DropdownMenu';
import TextAreaInput from '../../../../common/Form/Fields/TextAreaInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import resource_details_form_props from '../Forms/resource_details';
import rule_form_props from '../Forms/rules';
import { ResourcesContext } from '../ResourcesContext';
import useOnDelete from './hooks/useOnDeleteToggle';
import useOnDisableToggle from './hooks/useOnDisableToggle';
import useOnPauseToggle from './hooks/useOnPauseToggle';
import { StatusBadge, tooltip_status } from './StatusBadge';
type ConnectionPreviewField = 'name' | 'rules' | 'status' | 'open' | 'pause' | 'disable' | 'delete';
interface Props {
  connection: APIWebhook;
  fields?: ConnectionPreviewField[];
  mutate?: () => any;
  onDirtyChange?: (isDirty: boolean, values_diff: {
    initial_values: any;
    values: any;
  }) => void;
}
type CompareValue = string | object | undefined;
export const is_filter_rule_changed = (values_diff: {
  initial_values: any;
  values: any;
}): boolean => {
  const {
    initial_values,
    values
  } = values_diff;
  const compareStringsOrObjects = (a: CompareValue, b: CompareValue) => {
    if (typeof a !== typeof b) {
      return true;
    }
    return JSON.stringify(typeof a === 'string' ? JSON.parse(a) : a) !== JSON.stringify(typeof b === 'string' ? JSON.parse(b) : b);
  };
  const rule = values.rules.find(r => r.type === 'filter');
  const initial_rule = initial_values.rules.find(r => r.type === 'filter');
  if (!rule && !initial_rule) {
    return false;
  }
  if (!initial_rule && rule || !rule && initial_rule) {
    return true;
  }
  try {
    const is_body_changed = rule.body && compareStringsOrObjects(rule.body, initial_rule.body);
    const is_headers_changed = rule.headers && compareStringsOrObjects(rule.headers, initial_rule.headers);
    const is_path_changed = rule.path && compareStringsOrObjects(rule.path, initial_rule.path);
    const is_query_changed = rule.query && compareStringsOrObjects(rule.query, initial_rule.query);
    return is_body_changed || is_headers_changed || is_path_changed || is_query_changed;
  } catch (error) {
    return true;
  }
};
const ConnectionPreview = t0 => {
  const $ = _c(50);
  const {
    connection,
    fields,
    mutate,
    onDirtyChange
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    mutateResourceType
  } = useContext(ResourcesContext);
  const {
    addToast
  } = useToasts();
  const [error, setError] = useState(null);
  const onPause = useOnPauseToggle();
  const onDisable = useOnDisableToggle("webhooks", mutate);
  const onDelete = useOnDelete("webhooks", mutate);
  let t1;
  if ($[0] !== fields) {
    t1 = fields ? fields.includes("status") : true;
    $[0] = fields;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const show_status = t1;
  let t2;
  if ($[2] !== fields) {
    t2 = fields ? fields.includes("name") : true;
    $[2] = fields;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const show_name = t2;
  let t3;
  if ($[4] !== fields) {
    t3 = fields ? fields.includes("rules") : true;
    $[4] = fields;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const show_rules = t3;
  let t4;
  if ($[6] !== fields) {
    t4 = fields ? fields.includes("open") : true;
    $[6] = fields;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const show_open = t4;
  let t5;
  if ($[8] !== fields) {
    t5 = fields ? fields.includes("pause") : true;
    $[8] = fields;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const show_pause = t5;
  let t6;
  if ($[10] !== fields) {
    t6 = fields ? fields.includes("disable") : true;
    $[10] = fields;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const show_disable = t6;
  let t7;
  if ($[12] !== fields) {
    t7 = fields ? fields.includes("delete") : true;
    $[12] = fields;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const show_delete = t7;
  let t8;
  if ($[14] !== HookdeckAPI.webhooks || $[15] !== addToast || $[16] !== connection.id || $[17] !== mutate || $[18] !== mutateResourceType || $[19] !== onDirtyChange) {
    t8 = (values, t9) => {
      const {
        resetForm
      } = t9;
      return HookdeckAPI.webhooks.update(connection.id, {
        name: values.name || null,
        rules: values.rules ? rule_form_props.postprocessValues(values.rules) : []
      }).then(updated_connection => {
        const input_transform_rule_index = values.rules.findIndex(_temp);
        const output_transform_rule_index = updated_connection.rules.findIndex(_temp2);
        if (input_transform_rule_index > -1 && output_transform_rule_index > -1) {
          updated_connection.rules[output_transform_rule_index] = {
            ...values.rules[input_transform_rule_index],
            ...updated_connection.rules[output_transform_rule_index]
          };
        }
        mutateResourceType("webhooks", updated_connection);
        resetForm({
          values: {
            name: updated_connection.name || "",
            rules: rule_form_props.getInitialValues(updated_connection.rules)
          },
          touched: {}
        });
        mutate?.();
        addToast("success", "Connection updated");
        onDirtyChange?.(false, {
          initial_values: updated_connection,
          values: updated_connection
        });
        setError(null);
      }).catch(e => {
        addToast("error", `An error occurred while saving the connection${e.response?.data[0] ? `: ${e.response.data[0]}` : ""}`);
        setError(e);
      });
    };
    $[14] = HookdeckAPI.webhooks;
    $[15] = addToast;
    $[16] = connection.id;
    $[17] = mutate;
    $[18] = mutateResourceType;
    $[19] = onDirtyChange;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== HookdeckAPI || $[22] !== connection.name || $[23] !== connection.source) {
    t9 = async values_0 => {
      const errors = {};
      const webhook_name_errors = ((await resource_details_form_props.validate(values_0, name => !name || connection.name === name ? Promise.resolve(false) : HookdeckAPI.webhooks.nameIsUsed(connection.source.id, name), false)) as any)?.name;
      if (webhook_name_errors) {
        errors.name = webhook_name_errors;
      }
      const rules_errors = await rule_form_props.validate(values_0.rules, HookdeckAPI);
      if (rules_errors) {
        errors.rules = rules_errors;
      }
      return cleanseFormErrorObject(errors);
    };
    $[21] = HookdeckAPI;
    $[22] = connection.name;
    $[23] = connection.source;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = connection.name;
  let t11;
  if ($[25] !== connection.rules) {
    t11 = rule_form_props.getInitialValues(connection.rules);
    $[25] = connection.rules;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== connection.name || $[28] !== t11) {
    t12 = {
      name: t10,
      rules: t11
    };
    $[27] = connection.name;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== connection || $[31] !== error || $[32] !== fields || $[33] !== onDelete || $[34] !== onDirtyChange || $[35] !== onDisable || $[36] !== onPause || $[37] !== show_delete || $[38] !== show_disable || $[39] !== show_name || $[40] !== show_open || $[41] !== show_pause || $[42] !== show_rules || $[43] !== show_status) {
    t13 = formikProps => <ConnectionPreviewForm key={JSON.stringify(formikProps.initialValues)} connection={connection} error={error} fields={fields} onDirtyChange={onDirtyChange} show_status={show_status} show_name={show_name} show_rules={show_rules} show_open={show_open} show_pause={show_pause} show_disable={show_disable} show_delete={show_delete} {...formikProps} initial_values={formikProps.initialValues} values={formikProps.values} onPause={onPause} onDisable={onDisable} onDelete={onDelete} />;
    $[30] = connection;
    $[31] = error;
    $[32] = fields;
    $[33] = onDelete;
    $[34] = onDirtyChange;
    $[35] = onDisable;
    $[36] = onPause;
    $[37] = show_delete;
    $[38] = show_disable;
    $[39] = show_name;
    $[40] = show_open;
    $[41] = show_pause;
    $[42] = show_rules;
    $[43] = show_status;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t12 || $[46] !== t13 || $[47] !== t8 || $[48] !== t9) {
    t14 = <Formik onSubmit={t8} validate={t9} initialValues={t12}>{t13}</Formik>;
    $[45] = t12;
    $[46] = t13;
    $[47] = t8;
    $[48] = t9;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  return t14;
};
interface ConnectionPreviewFormProps {
  connection: APIWebhook;
  fields?: ConnectionPreviewField[];
  error: HookdeckAPIError | null;
  onDirtyChange?: (isDirty: boolean, values_diff: {
    initial_values: any;
    values: any;
  }) => void;
  show_status: boolean;
  show_name: boolean;
  show_rules: boolean;
  show_open: boolean;
  show_pause: boolean;
  show_disable: boolean;
  show_delete: boolean;
  onPause: (connection: APIWebhook, pause: boolean) => void;
  onDisable: (connection: APIWebhook, disable: boolean) => void;
  onDelete: (connection: APIWebhook) => void;
  dirty: boolean;
  initial_values: any;
  values: any;
  resetForm: (values?: any) => void;
  isSubmitting: boolean;
  isValid: boolean;
  submitCount: number;
}
const ConnectionPreviewForm: React.FC<ConnectionPreviewFormProps> = t0 => {
  const $ = _c(62);
  const {
    connection,
    fields,
    error,
    onDirtyChange,
    show_status,
    show_name,
    show_rules,
    show_open,
    show_pause,
    show_disable,
    show_delete,
    dirty,
    initial_values,
    values,
    resetForm,
    isSubmitting,
    isValid,
    onPause,
    onDisable,
    onDelete
  } = t0;
  let t1;
  if ($[0] !== dirty || $[1] !== initial_values || $[2] !== onDirtyChange || $[3] !== values) {
    t1 = () => {
      if (onDirtyChange) {
        if (dirty) {
          onDirtyChange(dirty, {
            initial_values,
            values
          });
        } else {
          onDirtyChange(false, {
            initial_values,
            values: initial_values
          });
        }
      }
    };
    $[0] = dirty;
    $[1] = initial_values;
    $[2] = onDirtyChange;
    $[3] = values;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== dirty || $[6] !== initial_values || $[7] !== values) {
    t2 = [dirty, initial_values, values];
    $[5] = dirty;
    $[6] = initial_values;
    $[7] = values;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  useEffect(t1, t2);
  let dropdown_options;
  if ($[9] !== connection || $[10] !== onDelete || $[11] !== onDisable || $[12] !== onPause || $[13] !== show_delete || $[14] !== show_disable || $[15] !== show_pause) {
    dropdown_options = [] as DropdownMenuOption[];
    if (show_pause) {
      let t3;
      if ($[17] !== connection || $[18] !== onPause) {
        t3 = connection.paused_at ? {
          label: "Unpause connection",
          icon: "play_circle",
          onClick: () => onPause(connection, false)
        } : {
          label: "Pause connection",
          icon: "pause_circle",
          onClick: () => onPause(connection, true)
        };
        $[17] = connection;
        $[18] = onPause;
        $[19] = t3;
      } else {
        t3 = $[19];
      }
      dropdown_options.push(t3);
    }
    if (show_disable) {
      let t3;
      if ($[20] !== connection || $[21] !== onDisable) {
        t3 = connection.disabled_at ? {
          label: "Enable connection",
          icon: "enable",
          onClick: () => onDisable(connection, false)
        } : {
          label: "Disable connection",
          icon: "disable",
          onClick: () => onDisable(connection, true)
        };
        $[20] = connection;
        $[21] = onDisable;
        $[22] = t3;
      } else {
        t3 = $[22];
      }
      dropdown_options.push(t3);
    }
    if (show_delete) {
      let t3;
      if ($[23] !== connection || $[24] !== onDelete) {
        t3 = {
          label: "Delete connection",
          icon: "delete",
          danger: true,
          onClick: () => onDelete(connection)
        };
        $[23] = connection;
        $[24] = onDelete;
        $[25] = t3;
      } else {
        t3 = $[25];
      }
      dropdown_options.push(t3);
    }
    $[9] = connection;
    $[10] = onDelete;
    $[11] = onDisable;
    $[12] = onPause;
    $[13] = show_delete;
    $[14] = show_disable;
    $[15] = show_pause;
    $[16] = dropdown_options;
  } else {
    dropdown_options = $[16];
  }
  let t3;
  let t4;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      vh: 70
    };
    t4 = {
      x: 4,
      t: 4
    };
    $[26] = t3;
    $[27] = t4;
  } else {
    t3 = $[26];
    t4 = $[27];
  }
  let t5;
  if ($[28] !== connection || $[29] !== show_status) {
    t5 = show_status && <Div p={{
      b: 4
    }} flex={{
      gap: 2,
      align: "center"
    }}><StatusBadge resource={connection} /><Tooltip tooltip={tooltip_status("connection", connection)} side="bottom"><Icon muted={true} icon="info" /></Tooltip></Div>;
    $[28] = connection;
    $[29] = show_status;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  const t6 = fields?.length === 1 && show_name ? 0 : 4;
  let t7;
  if ($[31] !== t6) {
    t7 = {
      b: t6
    };
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== show_name) {
    t8 = show_name && <TextAreaInput mono={true} name="name" label="Connection Name" auto_resize={true} placeholder="Enter a connection name or leave empty" format={field_formats.slugify} maxlength={155} />;
    $[33] = show_name;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== connection || $[36] !== show_name || $[37] !== show_rules) {
    t9 = show_rules && <><Text semi={true} muted={true} size="xs" m={{
        b: 1,
        t: show_rules && !show_name ? 0 : 4
      }}>Connection rules</Text><rule_form_props.Fields prefix="rules" webhook_id={connection.id} source_id={connection.source.id} connection_name={connection.name} display="compact" /></>;
    $[35] = connection;
    $[36] = show_name;
    $[37] = show_rules;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== error) {
    t10 = error && <Text danger={true} m={{
      t: 2
    }}>{typeof error.response === "string" ? error.response : error.response?.data?.[0] || "Fatal error"}</Text>;
    $[39] = error;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== t10 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t11 = <Div p={t7}>{t8}{t9}{t10}</Div>;
    $[41] = t10;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t11 || $[47] !== t5) {
    t12 = <StyledCardSection scroll={true} max_h={t3} p={t4}>{t5}{t11}</StyledCardSection>;
    $[46] = t11;
    $[47] = t5;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  let t14;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      x: 4,
      y: 3
    };
    t14 = {
      justify: "flex-end",
      gap: 3
    };
    $[49] = t13;
    $[50] = t14;
  } else {
    t13 = $[49];
    t14 = $[50];
  }
  let t15;
  if ($[51] !== connection || $[52] !== dirty || $[53] !== dropdown_options || $[54] !== isSubmitting || $[55] !== isValid || $[56] !== resetForm || $[57] !== show_open) {
    t15 = <StyledCardSection p={t13} flex={t14}>{dirty || !show_open && dropdown_options.length === 0 ? <>{dirty && <Button onClick={() => resetForm()} outline={true}>Cancel</Button>}<Button.Permission submit={true} primary={true} icon="save" disabled={isSubmitting || !isValid || !dirty}>{isSubmitting ? <Icon icon="loading" /> : "Save"}</Button.Permission></> : <>{show_open && <Button to={`/connections/${connection.id}`} outline={true} icon="open">Open</Button>}{dropdown_options.length > 0 && <DropdownMenu.Permission outline={true} portal={true} expand_icon="horizontal_more" placement="bottom-end" options={dropdown_options} />}</>}</StyledCardSection>;
    $[51] = connection;
    $[52] = dirty;
    $[53] = dropdown_options;
    $[54] = isSubmitting;
    $[55] = isValid;
    $[56] = resetForm;
    $[57] = show_open;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== t12 || $[60] !== t15) {
    t16 = <Form>{t12}{t15}</Form>;
    $[59] = t12;
    $[60] = t15;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  return t16;
};
export default ConnectionPreview;
function _temp(r) {
  return r.type === "transform";
}
function _temp2(r_0) {
  return r_0.type === "transform";
}